import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, Field, Input, Select, Textarea, SubmitButton } from "@/components/shared";
import { submitLead, formatApiError } from "@/lib/api";

export default function GuestCleaning() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", accommodation: "", service_type: "", preferred_date: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead("guest_cleaning", form);
      setDone(true);
      toast.success("Request received — we'll confirm your clean shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="guest-cleaning" className="scroll-mt-24 bg-sand-light" data-testid="guest-cleaning-section">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-24">
          <div>
            <SectionHead
              overline="For Guests Staying in Bermuda"
              title={<>Staying in an Airbnb or<br /><span className="italic text-bermuda-dark">vacation home?</span></>}
              copy="Most vacation rentals don't offer cleaning during your stay — we do. Love the privacy of an Airbnb but miss hotel housekeeping? We bring hotel service to your vacation rental: a refresh mid-stay, a full clean, or daily visits while you're here."
            />
            <div className="mt-10 space-y-0">
              {[
                ["Hotel-Style Stayover Refresh", "Love the privacy of an Airbnb but miss hotel housekeeping? A 45–90 minute refresh: beds made, fresh towels, bathroom and kitchen reset, trash out, living areas straightened — your rental feels freshly checked-in again.", "From $45"],
                ["Full House Clean", "Every room, kitchen and bathroom properly cleaned while you're out enjoying the island.", "From $60"],
                ["Daily Stayover Service", "The Stayover Refresh every day of your stay — true hotel living in a private home.", "From $45 / visit"],
              ].map(([title, copy, price], i) => (
                <Reveal key={title} delay={i * 0.06}>
                  <div className="flex gap-5 border-t border-navy/10 py-6 last:border-b">
                    <Sparkles size={16} className="mt-1 shrink-0 text-bermuda-dark" strokeWidth={1.5} />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-4">
                        <h4 className="font-serif text-xl text-navy">{title}</h4>
                        <span className="whitespace-nowrap font-mono text-sm text-bermuda-dark" data-testid={`gc-price-${i}`}>{price}</span>
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-navy/60">{copy}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
              <p className="mt-6 text-xs leading-relaxed text-navy/45">Guide pricing — the exact quote is confirmed by property size and scope before booking. Multi-day and weekly rates available.</p>
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-10">
              {done ? (
                <div className="py-10 text-center" data-testid="guest-cleaning-success">
                  <CheckCircle2 size={40} strokeWidth={1.2} className="mx-auto text-bermuda-dark" />
                  <h3 className="mt-6 font-serif text-3xl text-navy">Consider it handled.</h3>
                  <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-navy/60">
                    Your cleaning request has been received. We'll confirm availability and pricing personally, very shortly.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-2xl text-navy">Book a Mid-Stay Clean</h3>
                  <p className="mt-2 text-sm text-navy/55">Tell us where you're staying and what you'd like — we take care of the rest.</p>
                  <form onSubmit={onSubmit} className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="guest-cleaning-form">
                    <Field label="Your Name" required><Input data-testid="gc-name" required value={form.name} onChange={set("name")} /></Field>
                    <Field label="Email" required><Input data-testid="gc-email" required type="email" value={form.email} onChange={set("email")} /></Field>
                    <Field label="Phone / WhatsApp" required><Input data-testid="gc-phone" required type="tel" value={form.phone} onChange={set("phone")} /></Field>
                    <Field label="Preferred Date" required><Input data-testid="gc-date" required type="date" value={form.preferred_date} onChange={set("preferred_date")} /></Field>
                    <div className="sm:col-span-2">
                      <Field label="Where Are You Staying?" required><Input data-testid="gc-accommodation" required value={form.accommodation} onChange={set("accommodation")} placeholder="Property name, address or area" /></Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Service" required>
                        <Select data-testid="gc-service-type" required value={form.service_type} onChange={set("service_type")}>
                          <option value="">Select…</option>
                          <option>Hotel-style stayover refresh</option>
                          <option>Full house clean</option>
                          <option>Daily stayover service</option>
                        </Select>
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Anything We Should Know?"><Textarea data-testid="gc-notes" value={form.notes} onChange={set("notes")} placeholder="Access details, priorities, number of rooms…" /></Field>
                    </div>
                    <div className="sm:col-span-2">
                      <SubmitButton loading={loading} testid="guest-cleaning-submit">Request My Clean</SubmitButton>
                    </div>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
